import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table,
  Tabs,
  Tab,
  Button,
  Form,
  Row,
  Col,
  Modal,
  InputGroup
} from "react-bootstrap";
import { IonIcon } from "@ionic/react";
import { trash, syncOutline } from "ionicons/icons";
import {
  getAllScripts,
  getScriptById,
  getAllSettings,
  getScriptItemsByScript
} from "../../../data/getters";
import scriptApi from "../../../data/api/scriptApi";
import { AxesViewer } from "@babylonjs/core/Debug/axesViewer";
import scriptItemApi from "../../../data/api/scriptItemApi";
import {
  deleteScriptItem,
  addScriptItem
} from "../../../data/store/actions/scriptStoreFunctions";
import Loading from "../../helpers/Loading";
import {
  Engine,
  Scene,
  Vector3,
  MeshBuilder,
  ArcRotateCamera,
  HemisphericLight,
  StandardMaterial,
  Color3,
  Color4,
  Quaternion,
  Matrix
} from "@babylonjs/core";

import {
  parseSettingString,
  serializeSettingArray
} from "../scriptsPageutil/serializeSettings";
import {
  extractRawList,
  evaluateFormula,
  parseConditionalPosition
} from "../scriptsPageutil/evalArr";
import ObjectViewer from "../../../model/ObjectViewer";
import processScript from "../../../calculation/itemGenerator/processScript";
import { useLanguage } from "../../helpers/LanguageContext";
import { useNavigationGuard } from "../../helpers/NavigationGuardContext";
import PositionRuleBuilder from "./PositionRuleBuilder";
import QuantityRuleBuilder from "./QuantityRuleBuilder";
import ScriptItemsCards from "./ScriptItemsCards";
import ScriptMainDataPanel from "./ScriptMainDataPanel";
import EditableTitle from "./EditableTitle";
import {
  createEmptyScript,
  createEmptyScriptItem,
  createEditableScriptItem,
  createPendingScriptItem,
  createScriptSaveData,
  DEFAULT_USER_DIMENSIONS,
  getScriptItemKey,
  getScriptItemSignature
} from "./scriptEditorUtils";
// Custom backdrop CSS
const style = `
  .custom-backdrop {
    background-color: rgba(0, 0, 0, 0.2);
  }
  .script-item-editor-modal {
    z-index: 20000 !important;
  }
  .script-item-editor-backdrop {
    z-index: 19990 !important;
    background-color: rgba(0, 0, 0, 0.35) !important;
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
  }
  .script-item-card {
    transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
  }
  .script-item-card:hover,
  .script-item-card:focus-visible {
    transform: translateY(-4px);
    border-color: var(--bs-primary);
    box-shadow: 0 0.75rem 1.5rem rgba(0, 0, 0, 0.14) !important;
    outline: none;
  }
`;
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = style;
document.head.appendChild(styleSheet);

export default function ScriptEditor({ onClose }) {
  const { scriptId } = useParams();
  const isEditing = !!scriptId;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scripts = useSelector((state) => state.scripts || []);
  const settingsList = useSelector((state) => state.settings || []);
  const scriptItems = useSelector((state) => {
    const all = state.scriptItems || [];
    return scriptId
      ? all
          .filter((it) => String(it.scriptId) == String(scriptId))
          .filter((it) => it.itemId !== -1)
      : [];
  });

  const { t } = useLanguage();
  const { registerGuard } = useNavigationGuard();
  const [positionDSL, setPositionDSL] = useState("");
  const [rotationDSL, setRotationDSL] = useState("");
  const [itemEditorTab, setItemEditorTab] = useState("general");
  const [editingSettingId, setEditingSettingId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const showEditor = true;
  const [scriptList, setScriptList] = useState([]);
  const [script, setScript] = useState(createEmptyScript);
  const [scriptImageFile, setScriptImageFile] = useState(null);
  const [removeScriptImage, setRemoveScriptImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const scriptFileInputRef = useRef();
  const [autoIterate, setAutoIterate] = useState(false);
  const [parsedSettings, setParsedSettings] = useState([]);
  const [newItem, setNewItem] = useState(createEmptyScriptItem);
  const [isPositionRuleValid, setIsPositionRuleValid] = useState(true);
  const [isRotationRuleValid, setIsRotationRuleValid] = useState(true);
  const [isSizeRuleValid, setIsSizeRuleValid] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [userDimensions, setUserDimensions] = useState(
    DEFAULT_USER_DIMENSIONS
  );
  const [showSettingModal, setShowSettingModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [showSettingsList, setShowSettingsList] = useState(false);
  const [newSettingPair, setNewSettingPair] = useState({ id: "", value: "" });
  const [hiddenItems, setHiddenItems] = useState(new Set());

  const toggleVisibility = (item) => {
    setHiddenItems((prev) => {
      const next = new Set(prev);
      const key = getScriptItemKey(item);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  useEffect(() => {
    if (script.imageData && script.imageContentType) {
      setPreviewImage(
        `data:${script.imageContentType};base64,${script.imageData}`
      );
    } else {
      setPreviewImage(null);
    }
  }, [script.imageData, script.imageContentType]);

  const onScriptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext("2d");
        // teljes kép beleszűkítése a nagyobb előnézethez
        ctx.drawImage(img, 0, 0, 512, 512);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
        // base64 adat kinyerése
        const base64 = dataUrl.split(",")[1];
        setPreviewImage(dataUrl);
        setScript((prev) => ({
          ...prev,
          imageData: base64,
          imageContentType: "image/jpeg"
        }));
        setScriptImageFile(file);
        setRemoveScriptImage(false);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        await dispatch(getAllScripts());

        await dispatch(getAllSettings());

        if (isEditing) {
          const s = await dispatch(getScriptById(scriptId));

          setScript({
            name: s.name || "",
            room: s.room || "",
            setting: s.setting || "",
            imageData: s.imageData || null,
            imageContentType: s.imageContentType || null
          });

          setParsedSettings(
            s.setting ? s.setting.split(",").map((p) => p) : []
          );
          if (s.setting) {
            setParsedSettings(parseSettingString(s.setting));
          }
          await scriptItemApi.getAllScriptItemsForScriptApi(scriptId);
          await dispatch(getScriptItemsByScript(scriptId));
        }
      } catch (err) {
        setError("Hiba az adatok betöltésekor");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, scriptId, isEditing]);
  // 🔹 ScriptCaller-szerű dimenzió-feltöltés defaultokkal
  useEffect(() => {
    if (!isEditing) return;
    if (!settingsList || settingsList.length === 0) return;

    // 1) script.setting -> { settingId: value } objektum
    const parsed = script.setting ? parseSettingString(script.setting) : [];
    const cfg = Object.fromEntries(
      parsed.map(({ settingId, value }) => [String(settingId), String(value)])
    );

    // 2) hiányzó settingek feltöltése a settingsList első (default) értékével
    const cfgWithDefaults = (settingsList || []).reduce(
      (acc, s) => {
        const key = String(s.settingId);
        const hasValue = acc[key] != null && acc[key] !== "";
        if (!hasValue && Array.isArray(s.valueList) && s.valueList.length > 0) {
          acc[key] = String(s.valueList[0]);
        }
        return acc;
      },
      { ...cfg }
    );

    // 3) ha a configban szerepel width/height/depth, vegyük át
    const w = Number(cfgWithDefaults.width);
    const h = Number(cfgWithDefaults.height);
    const d = Number(cfgWithDefaults.depth);

    setUserDimensions((prev) => ({
      width: Number.isFinite(w) ? w : prev.width,
      height: Number.isFinite(h) ? h : prev.height,
      depth: Number.isFinite(d) ? d : prev.depth
    }));
  }, [isEditing, script.setting, settingsList]);

  useEffect(() => setScriptList(scripts), [scripts]);

  // after you’ve loaded scriptItems and scripts into state
  useEffect(() => {
    if (!scriptItems.length || !scripts.length) return;

    // start from whatever settings you already have
    let merged = [...parsedSettings];

    scriptItems.forEach((item) => {
      if (!item.refScript) return;
      // find that referenced script
      const parent = scripts.find(
        (s) => String(s.scriptId) === String(item.refScript)
      );
      if (!parent?.setting) return;

      // parse its defaults
      const defaults = parseSettingString(parent.setting);
      defaults.forEach((def) => {
        if (!merged.some((s) => s.settingId === def.settingId)) {
          merged.push(def);
        }
      });
    });

    // only update if we actually added something
    if (merged.length !== parsedSettings.length) {
      setParsedSettings(merged);
      // also keep your serialized `script.setting` in sync
      setScript((prev) => ({
        ...prev,
        setting: serializeSettingArray(merged)
      }));
    }
  }, [scriptItems, scripts]);

  const refParsedSettings = useMemo(() => {
    if (!newItem.refScript) return [];
    const parent = scripts.find(
      (s) => String(s.scriptId) === String(newItem.refScript)
    );
    return parent ? parseSettingString(parent.setting || "") : [];
  }, [newItem.refScript, scripts]);

  const rawItems = useMemo(() => {
    const deleted = pendingItems
      .filter((it) => it.itemId === -1)
      .map((it) => it.originalItemId)
      .filter(Boolean);

    const pendingIds = pendingItems
      .filter((it) => it.itemId !== -1)
      .map((it) => it.itemId)
      .filter((id) => id !== -1);

    const kept = scriptItems.filter(
      (it) => !deleted.includes(it.itemId) && !pendingIds.includes(it.itemId)
    );

    // Csak az újak (tempId) és a létezők frissítései (itemId > 0)
    const additions = pendingItems.filter(
      (it) => it.tempId !== undefined || it.itemId !== -1
    );

    return [...kept, ...additions].map((it) => ({
      ...it,
      isPending: it.tempId != null,
      rawSize: it.size,
      name: it.name,
      rawPosition: it.position.includes("?")
        ? [it.position]
        : extractRawList(it.position),
      rawRotation: extractRawList(it.rotation)
    }));
  }, [scriptItems, pendingItems]);

  const allScriptItems = useSelector((state) => state.scriptItems || []);

  useEffect(() => {
    const refs = Array.from(
      new Set(
        rawItems
          .map((it) => it.refScript)
          .filter((id) => id !== undefined && id !== null && id !== "")
      )
    );
    if (refs.length === 0) return;
    refs.forEach((refId) => {
      dispatch(getScriptItemsByScript(refId));
    });
  }, [dispatch, rawItems]);

  const processedItems = useMemo(() => {
    const byScript = allScriptItems.reduce((acc, it) => {
      acc[it.scriptId] = acc[it.scriptId] || [];
      acc[it.scriptId].push(it);
      return acc;
    }, {});

    function expandItem(item, parentSettings, parentSize) {
      // Normalize settings array
      const settingsArr = Array.isArray(parentSettings)
        ? parentSettings
        : Object.entries(parentSettings).map(([settingId, value]) => ({
            settingId,
            value: String(value)
          }));

      // 1) SIZE: handle ternary and macros in one go
      const size = (parseConditionalPosition(
        item.size || "[1,1,1]",
        settingsArr,
        parentSize,
        1
      ) || [[1, 1, 1]])[0];

      // 2) QTY
      const qtyExpr = evaluateFormula(
        item.qty,
        Object.fromEntries(settingsArr.map((s) => [s.settingId, s.value]))
      );
      const n = Math.max(1, Math.round(new Function(`return (${qtyExpr})`)()));

      // 3) POSITION: ternary + i(...) expansion
      const relPositions =
        parseConditionalPosition(
          item.position || "[0,0,0]",
          settingsArr,
          parentSize,
          n
        ) || Array.from({ length: n }, () => [0, 0, 0]);
      const relRotations =
        parseConditionalPosition(
          item.rotation || "[0,0,0]",
          settingsArr,
          parentSize,
          n
        ) || Array.from({ length: n }, () => [0, 0, 0]);

      // 5) Pivot for nested transforms
      const pivot = new Vector3(size[0] / 2, size[1] / 2, size[2] / 2);

      // 6) No refScript: return direct instances
      if (!item.refScript) {
        return relPositions.map((pos, idx) => ({
          ...item,
          qty: 1,
          evaluatedSize: size,
          evaluatedPosition: [pos],
          evaluatedRotation: [relRotations[idx]]
        }));
      }

      // 7) Handle nested refScripts (rest of logic unchanged)
      const children = byScript[item.refScript] || [];
      const defaultSettings = parseSettingString(item.refSettings) || [];
      const instances = [];

      relPositions.forEach((pPos, idx) => {
        const pRot = relRotations[idx] || [0, 0, 0];
        const pQuat = Quaternion.RotationYawPitchRoll(
          (pRot[1] * Math.PI) / 2,
          (pRot[0] * Math.PI) / 2,
          (pRot[2] * Math.PI) / 2
        );
        const pMat = Matrix.Identity();
        pQuat.toRotationMatrix(pMat);

        children.forEach((child) => {
          //   const adds = expandItem(child, defaultSettings, size);
          const mergedSettings = [
            ...settingsArr,
            ...(defaultSettings || [])
          ].filter(
            // dedupe same settingId, keep the refScript’s override last
            (v, i, self) =>
              self.findIndex((x) => x.settingId === v.settingId) === i
          );
          const adds = expandItem(child, mergedSettings, size) || [];
          adds.forEach((inst) => {
            const posList = Array.isArray(inst.evaluatedPosition)
              ? inst.evaluatedPosition
              : [];
            const rotList = Array.isArray(inst.evaluatedRotation)
              ? inst.evaluatedRotation
              : [];
            inst.evaluatedPosition = posList.map((p) => {
              const [x = 0, y = 0, z = 0] = Array.isArray(p) ? p : [0, 0, 0];
              const v0 = new Vector3(x, y, z).subtract(pivot);
              const v1 = Vector3.TransformCoordinates(v0, pMat);
              const v2 = v1.add(pivot).add(new Vector3(...pPos));
              return [v2.x, v2.y, v2.z];
            });
            inst.evaluatedRotation = rotList.map((r) => {
              const [cx = 0, cy = 0, cz = 0] = Array.isArray(r) ? r : [0, 0, 0];
              const cQuat = Quaternion.RotationYawPitchRoll(
                (cy * Math.PI) / 2,
                (cx * Math.PI) / 2,
                (cz * Math.PI) / 2
              );
              const combo = pQuat.multiply(cQuat).toEulerAngles();
              return [
                Math.round(combo.x / (Math.PI / 2)) % 4,
                Math.round(combo.y / (Math.PI / 2)) % 4,
                Math.round(combo.z / (Math.PI / 2)) % 4
              ];
            });
            instances.push(inst);
          });
        });
      });

      return instances;
    }
    let all = [];

    rawItems.forEach((raw) => {
      all.push(
        ...expandItem(
          raw, // <-- no autoIterate override here
          parsedSettings,
          [userDimensions.width, userDimensions.height, userDimensions.depth],
          raw.qty // <-- always the raw qty
        )
      );
    });

    console.log("all: ", all);
    console.log("------------------------------------------");

    const seen = new Set();
    return all.filter((inst) => {
      const uniqueId = inst.itemId !== -1 ? inst.itemId : inst.tempId;
      const key = uniqueId + "|" + JSON.stringify(inst.evaluatedPosition);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawItems, allScriptItems, parsedSettings, userDimensions]);

  const configForProcess = useMemo(() => {
    const base = Object.fromEntries(
      parsedSettings.map((s) => [String(s.settingId), String(s.value)])
    );
    return {
      ...base,
      width: userDimensions.width,
      height: userDimensions.height,
      depth: userDimensions.depth
    };
  }, [parsedSettings, userDimensions]);

  const generatedFromProcess = useMemo(() => {
    if (!scriptId) return [];
    try {
      const dims = {
        width: userDimensions.width,
        height: userDimensions.height,
        depth: userDimensions.depth
      };

      // ugyanaz a pipeline, mint a ScriptCaller-ben
      const out = processScript(configForProcess, dims, Number(scriptId));

      // ugyanaz a szín-mappolás: PFL -> -1
      return out.map((it) => ({
        ...it,
        colorId: it.material === "PFL" ? -1 : (it.colorId ?? null)
      }));
    } catch (e) {
      console.error("processScript error:", e);
      return [];
    }
    // fontos: ha a store-ban frissülnek a script itemek, újraszámoljon
  }, [configForProcess, userDimensions, scriptId, allScriptItems]);

  const visibleGeneratedItems = useMemo(() => {
    const hiddenSignatures = new Set(
      rawItems
        .filter((item) =>
          hiddenItems.has(getScriptItemKey(item))
        )
        .map(getScriptItemSignature)
    );

    return generatedFromProcess.filter(
      (item) =>
        !hiddenSignatures.has(getScriptItemSignature(item))
    );
  }, [generatedFromProcess, hiddenItems, rawItems]);

  const handleScriptChange = (e) =>
    setScript((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleDimensionChange = (e) =>
    setUserDimensions((prev) => ({
      ...prev,
      [e.target.name]: Number(e.target.value)
    }));
  const expressionSettings = parsedSettings.map((setting) => ({
    ...setting,
    name: settingsList.find((candidate) => String(candidate.settingId) === String(setting.settingId))?.name || ""
  }));
  const handlePositionRuleChange = useCallback((positionRuleDsl) => {
    setNewItem((prev) =>
      prev.positionRuleDsl === positionRuleDsl
        ? prev
        : { ...prev, positionRuleDsl }
    );
  }, []);
  const handlePositionRuleValidityChange = useCallback(
    (isValid) => setIsPositionRuleValid(isValid),
    []
  );

  const handleRotationRuleChange = useCallback((rotationRuleDsl) => {
    setNewItem((previous) => ({ ...previous, rotationRuleDsl }));
  }, []);
  const handleSizeRuleChange = useCallback((size) => {
    setNewItem((previous) => ({ ...previous, size }));
  }, []);

  const addItem = () => {
    if (!isSizeRuleValid || !isPositionRuleValid || !isRotationRuleValid) {
      setItemEditorTab(!isSizeRuleValid ? "size" : !isPositionRuleValid ? "position" : "rotation");
      window.alert("A szabály hibás. Javítsd a jelzett mezőket.");
      return;
    }
    const item = createPendingScriptItem({
      item: newItem,
      settings: parsedSettings,
      autoIterate,
      positionDSL,
      rotationDSL
    });

    // Betesszük a pendingItems-be (szerkesztés vagy újdonság kezelése)
    /*   setPendingItems((prev) => {
      if (editingIndex !== null && rawItems[editingIndex]) {
        const original = rawItems[editingIndex];
        const idx = prev.findIndex(
          (p) => p.itemId === original.itemId || p.tempId === original.tempId
        );
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = item;
          return copy;
        }
      }
      return [...prev, item];
    });*/

    setPendingItems((prev) => {
      if (editingItemId !== null) {
        const idx = prev.findIndex(
          (p) =>
            p.itemId === editingItemId ||
            (p.tempId !== undefined && p.tempId === editingItemId)
        );
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = item;
          return copy;
        }
      }
      return [...prev, item];
    });

    setNewItem((prev) =>
      createEmptyScriptItem({
        refScript: prev.refScript,
        refSettings: prev.refSettings,
        positionRuleDsl: prev.positionRuleDsl
      })
    );

    setEditingIndex(null);
    setEditingItemId(null);
    setShowItemModal(false);
  };

  const handleRemoveScriptImage = () => {
    setPreviewImage(null);
    setScriptImageFile(null);
    setRemoveScriptImage(true);
    setScript((previous) => ({
      ...previous,
      imageData: null,
      imageContentType: null
    }));
    if (scriptFileInputRef.current) {
      scriptFileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const unregister = registerGuard(async () =>
      window.confirm(t("confirmCloseScript"))
    );
    return unregister;
  }, [registerGuard, t]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const openNewItemEditor = () => {
    setEditingIndex(null);
    setEditingItemId(null);
    setAutoIterate(false);
    setPositionDSL("");
    setRotationDSL("");
    setNewItem(createEmptyScriptItem());
    setItemEditorTab("general");
    setShowItemModal(true);
  };

  const openItemEditor = (item, index) => {
    setEditingItemId(item.itemId > 0 ? item.itemId : item.tempId);
    setEditingIndex(index);
    setAutoIterate(false);
    setPositionDSL("");
    setRotationDSL("");
    setNewItem(createEditableScriptItem(item));
    setItemEditorTab("general");
    setShowItemModal(true);
  };

  const closeItemEditor = () => {
    setShowItemModal(false);
    setEditingIndex(null);
    setEditingItemId(null);
  };

  useEffect(() => {
    if (editingIndex !== null && !rawItems[editingIndex]) {
      setEditingIndex(null);
    }
  }, [rawItems, editingIndex]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!script.name.trim() || !script.room.trim()) {
      return setError("Kérlek, töltsd ki a Name és a Room mezőket is!");
    }

    const scriptData = createScriptSaveData(script);

    try {
      let usedScriptId = scriptId;

      if (isEditing) {
        try {
          await dispatch(
            scriptApi.updateScriptApi(
              { script_id: scriptId, ...scriptData },
              scriptImageFile,
              removeScriptImage
            )
          );
        } catch (err) {
          if (
            !(err.status === 400 && err.data?.message.includes("no changes"))
          ) {
            throw err;
          }
        }
      } else {
        const newS = await dispatch(
          scriptApi.createScriptApi(scriptData, scriptImageFile)
        );
        usedScriptId = newS.scriptId;
      }

      for (const it of pendingItems) {
        const posStr = Array.isArray(it.position)
          ? it.position.join(",")
          : it.position;
        const rotStr = Array.isArray(it.rotation)
          ? it.rotation.join(",")
          : it.rotation;

        if (it.itemId === -1 && it.originalItemId) {
          await dispatch(scriptItemApi.deleteScriptItemApi(it.originalItemId));
          dispatch(deleteScriptItem(it.originalItemId));
          dispatch(getScriptItemsByScript(usedScriptId));
        } else if (it.itemId > 0) {
          await dispatch(
            scriptItemApi.updateScriptItemApi({
              itemId: it.itemId,
              script_id: usedScriptId,
              qty: it.qty,
              size: it.size,
              position: posStr,
              rotation: rotStr,
              refScript: it.refScript,
              refSettings: it.refSettings,
              name: it.name,
              kant: it.kant,
              rotable: it.rotable,
              material: it.material,
              details: it.details
            })
          );
        } else {
          console.log("New item data:", {
            script_id: usedScriptId,
            qty: it.qty,
            size: it.size,
            position: posStr,
            rotation: rotStr,
            refScript: it.refScript,
            refSettings: it.refSettings,
            name: it.name,
            kant: it.kant,
            rotable: it.rotable
          });

          const created = await dispatch(
            scriptItemApi.createScriptItemApi(
              {
                script_id: usedScriptId,
                qty: it.qty,
                size: it.size,
                position: posStr,
                rotation: rotStr,
                refScriptId: it.refScript,
                refSettings: it.refSettings,
                name: it.name,
                kant: it.kant,
                rotable: it.rotable,
                material: it.material,
                details: it.details
              },
              newItem.file
            )
          );

          setPendingItems((prev) =>
            prev.map((p) =>
              p.tempId === it.tempId ? { ...p, itemId: created.itemId } : p
            )
          );

          dispatch(addScriptItem(created));
          dispatch(getScriptItemsByScript(usedScriptId));
        }
      }

      setPendingItems([]);
      onClose();
      await dispatch(getAllScripts());
      await dispatch(getScriptItemsByScript(usedScriptId));
      navigate("/scripts");
    } catch (err) {
      setError("Hiba a script mentésekor");
    }
  };

  useEffect(() => {
    if (!showEditor || loading || !canvasRef.current) return;

    const engine = new Engine(canvasRef.current, true);
    const scene = new Scene(engine);
    scene.clearColor = new Color4(1, 1, 1, 1);
    sceneRef.current = scene;

    const camera = new ArcRotateCamera(
      "camera",
      Math.PI / 2,
      Math.PI / 4,
      2,
      Vector3.Zero(),
      scene
    );
    camera.minZ = 0.1;
    camera.maxZ = 10000;
    camera.attachControl(canvasRef.current, false);
    camera.wheelPrecision = 50;

    new HemisphericLight("light", new Vector3(0, 1, 0), scene);

    const originSphere = MeshBuilder.CreateSphere(
      "originSphere",
      { diameter: 500 },
      scene
    );
    originSphere.position = Vector3.Zero();
    const originMat = new StandardMaterial("originMat", scene);
    originMat.diffuseColor = new Color3(1, 0, 0);
    originSphere.material = originMat;

    // 2) vagy ha inkább tengelyeket is szeretnél látni:
    new AxesViewer(scene, 250);

    engine.runRenderLoop(() => scene.render());
    return () => engine.dispose();
  }, [showEditor, loading]);
  /********************************************* */
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    // régi meshek törlése (kivéve a floor-t)
    scene.meshes.filter((m) => m.name !== "floor").forEach((m) => m.dispose());

    const { width, height, depth } = userDimensions;
    const maxDim = Math.max(width, height, depth) || 1;
    const globalScale = 1 / maxDim;

    //    processedItems.forEach((it, idx) => {
    processedItems
      .filter((it) => !hiddenItems.has(it.itemId > 0 ? it.itemId : it.tempId))
      .forEach((it, idx) => {
        const [w, h, d] = it.evaluatedSize;
        const positions = it.evaluatedPosition;
        const rotations = it.evaluatedRotation;

        for (let j = 0; j < it.qty; j++) {
          const rawPos = positions[j] || [0, 0, 0];
          const rawRot = rotations[j] || [0, 0, 0];

          const sw = w * globalScale;
          const sh = h * globalScale;
          const sd = d * globalScale;
          const px = rawPos[0] * globalScale;
          const py = rawPos[1] * globalScale;
          const pz = rawPos[2] * globalScale;

          const box = MeshBuilder.CreateBox(
            `box_${idx}_${j}`,
            { width: sw, height: sh, depth: sd },
            scene
          );

          box.setPivotPoint(new Vector3(-sw / 2, -sh / 2, -sd / 2));
          box.position = new Vector3(px + sw / 2, py + sh / 2, pz + sd / 2);

          // forgatás a pivot körül
          box.rotation = new Vector3(
            (rawRot[0] * Math.PI) / 2,
            (rawRot[1] * Math.PI) / 2,
            (rawRot[2] * Math.PI) / 2
          );
          const mat = new StandardMaterial(`mat_${idx}_${j}`, scene);
          const col = Color3.FromHexString(it.material || "#4c4c4c");
          mat.diffuseColor = col;
          box.material = mat;
        }
      });
  }, [processedItems, userDimensions, parsedSettings, hiddenItems]);

  if (loading) return <Loading />;
  if (error) return <div className="alert alert-danger">{error}</div>;

  const availableSettings = settingsList.filter(
    (s) =>
      !parsedSettings.some((ps) => Number(ps.settingId) === Number(s.settingId))
  );

  const handleRemoveSetting = async (id) => {
    const updated = parsedSettings.filter(
      (s) => String(s.settingId) !== String(id)
    );
    setParsedSettings(updated);
    setScript((prev) => ({
      ...prev,
      setting: serializeSettingArray(updated)
    }));
    if (isEditing) {
      try {
        await dispatch(
          scriptApi.updateScriptApi(
            {
              script_id: scriptId,
              name: script.name,
              room: script.room,
              setting: serializeSettingArray(updated)
            },
            newItem.file
          )
        );
      } catch (err) {}
    }
  };

  const handleDeleteItem = (item) => {
    if (item.itemId > 0) {
      setPendingItems((prev) => [
        ...prev,
        { originalItemId: item.itemId, itemId: -1 }
      ]);
    } else {
      setPendingItems((prev) => prev.filter((p) => p.tempId !== item.tempId));
    }
  };

  const saveSettingPair = () => {
    const { id, value } = newSettingPair;
    if (!id) {
      alert("Adj meg érvényes ID-t!");
      return;
    }
    if (editingSettingId) {
      // Frissítés
      const updated = parsedSettings.map((s) =>
        String(s.settingId) === String(editingSettingId)
          ? { settingId: String(id), value: String(value) }
          : s
      );
      setParsedSettings(updated);
      setEditingSettingId(null);
    } else {
      // Új hozzáadása
      if (parsedSettings.some((s) => String(s.settingId) === String(id))) {
        alert("Ez a setting ID már létezik.");
        return;
      }
      setParsedSettings((prev) => [
        ...prev,
        { settingId: String(id), value: String(value) }
      ]);
    }
    // szinkronizálás a script objektummal
    setScript((prev) => ({
      ...prev,
      setting: serializeSettingArray(
        editingSettingId
          ? parsedSettings.map((s) =>
              String(s.settingId) === String(editingSettingId)
                ? { settingId: String(id), value: String(value) }
                : s
            )
          : [...parsedSettings, { settingId: String(id), value: String(value) }]
      )
    }));
    setShowSettingModal(false);
    setNewSettingPair({ id: "", value: "" });
  };

  return (
    <div className="container" style={{ paddingTop: "1rem" }}>
      <h1>{t("scripts")}</h1>

      <>
          <Row className="g-3 mb-4">
            <Col xs={12} lg={4}>
              <ScriptMainDataPanel
                handleDimensionChange={handleDimensionChange}
                handleScriptChange={handleScriptChange}
                handleSubmit={handleSubmit}
                onRemoveScriptImage={handleRemoveScriptImage}
                onScriptFileChange={onScriptFileChange}
                previewImage={previewImage}
                script={script}
                scriptFileInputRef={scriptFileInputRef}
                t={t}
                userDimensions={userDimensions}
              />
            </Col>
            <Col xs={12} lg={8}>
              <div
                className="border rounded shadow-sm overflow-hidden"
                style={{ width: "100%", height: "100%", minHeight: "55vh" }}
              >
              {visibleGeneratedItems.length ? (
                <ObjectViewer
                  object={{
                    objectId: 0,
                    position: "[0,0,0]",
                    rotation: "[0,0,0]"
                  }}
                  createdItems={visibleGeneratedItems}
                  usedColors={[]}
                  onItemUpdate={() => {}}
                />
              ) : (
                <canvas
                  ref={canvasRef}
                  style={{ width: "100%", height: "100%" }}
                />
              )}
              </div>
            </Col>
          </Row>

          <Modal
            show={showItemModal}
            onHide={closeItemEditor}
            size="xl"
            centered
            scrollable
            className="script-item-editor-modal"
            backdropClassName="script-item-editor-backdrop"
          >
            <Modal.Header closeButton>
              <Modal.Title as="div" className="flex-grow-1 me-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <EditableTitle
                    editLabel={t("editName")}
                    type="text"
                    value={newItem.name}
                    onChange={(event) =>
                      setNewItem((previous) => ({
                        ...previous,
                        name: event.target.value
                      }))
                    }
                    placeholder={t("name")}
                    aria-label={t("name")}
                    className="border-0 shadow-none px-0 fs-4 fw-semibold bg-transparent"
                  />
                  <EditableTitle
                    showIcon={false}
                    type="text"
                    value={newItem.material}
                    onChange={(event) =>
                      setNewItem((previous) => ({
                        ...previous,
                        material: event.target.value
                      }))
                    }
                    placeholder={t("material")}
                    aria-label={t("material")}
                    className="border-0 shadow-none px-0 fs-4 bg-transparent"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant={newItem.rotable ? "primary" : "outline-secondary"}
                    className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    role="switch"
                    aria-checked={Boolean(newItem.rotable)}
                    aria-label={t("rotatable")}
                    title={t("rotatable")}
                    onClick={() => setNewItem((previous) => ({ ...previous, rotable: !previous.rotable }))}
                  >
                    <IonIcon icon={syncOutline} aria-hidden="true" style={{ fontSize: "1.25rem" }} />
                  </Button>
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Tabs id="script-item-editor-tabs" activeKey={itemEditorTab} onSelect={setItemEditorTab} className="mb-3">
                <Tab eventKey="general" title={t("generalData")}>



              <Form.Group
                as={Row}
                className="mb-3"
                controlId="newItemRefScript"
              >
                <Form.Label column sm={3}>
                  {t("referenceScript")}
                </Form.Label>
                <Col sm={9}>
                  <Form.Select
                    name="refScript"
                    value={newItem.refScript}
                    onChange={(e) => {
                      const sel = e.target.value;
                      const scriptObj = scripts.find(
                        (s) => String(s.scriptId) === sel
                      );
                      const defaults = scriptObj
                        ? parseSettingString(scriptObj.setting)
                        : [];

                      // 1) seed the New‑Item’s own overrides
                      setNewItem((prev) => ({
                        ...prev,
                        refScript: sel,
                        refSettings: defaults.map(({ settingId, value }) => ({
                          settingId,
                          value
                        }))
                      }));

                      // 2) now *also* add any of those defaults into the main script’s settings
                      setParsedSettings((prevSettings) => {
                        const merged = [...prevSettings];
                        defaults.forEach((d) => {
                          if (
                            !merged.some((s) => s.settingId === d.settingId)
                          ) {
                            merged.push(d);
                          }
                        });
                        return merged;
                      });

                      // 3) and update the serialized `script.setting` so it sticks when you save
                      setScript((prev) => {
                        const all = [
                          ...(prev.setting
                            ? parseSettingString(prev.setting)
                            : []),
                          ...defaults
                        ].filter(
                          (s, i, arr) =>
                            arr.findIndex(
                              (x) => x.settingId === s.settingId
                            ) === i
                        );
                        return {
                          ...prev,
                          setting: serializeSettingArray(all)
                        };
                      });
                    }}
                  >
                    <option value="">— none —</option>
                    {scriptList.map((s) => (
                      <option key={s.scriptId} value={s.scriptId}>
                        {s.name} (ID: {s.scriptId})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>

              {newItem.refScript && (
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="newItemRefSettingsList"
                >
                  <Form.Label column sm={3}>
                    {t("referencedScriptSettings")}
                  </Form.Label>
                  <Col sm={9}>
                    {refParsedSettings.length === 0 ? (
                      <div className="text-muted">
                        {t("noAssignableSettings")}
                      </div>
                    ) : (
                      refParsedSettings.map((ps) => {
                        const settingName = settingsList.find(
                          (setting) => String(setting.settingId) === String(ps.settingId)
                        )?.name || ps.name || `${t("setting")} #${ps.settingId}`;
                        const override = newItem.refSettings.find(
                          (r) => r.settingId === ps.settingId
                        );
                        return (
                          <InputGroup className="mb-2" key={ps.settingId}>
                            <InputGroup.Text>{settingName}</InputGroup.Text>
                            <Form.Control
                              type="text"
                              aria-label={settingName}
                              value={override?.value ?? ps.value}
                              onChange={(e) => {
                                const val = e.target.value;
                                setNewItem((prev) => {
                                  const others = prev.refSettings.filter(
                                    (r) => r.settingId !== ps.settingId
                                  );
                                  return {
                                    ...prev,
                                    refSettings: [
                                      ...others,
                                      {
                                        settingId: ps.settingId,
                                        value: val
                                      }
                                    ]
                                  };
                                });
                              }}
                            />
                          </InputGroup>
                        );
                      })
                    )}
                  </Col>
                </Form.Group>
              )}
              {!newItem.refScript && (
                <div>
                  <Row className="mb-3 align-items-center">
                    <Col xs={3}>
                      <Form.Label className="mb-0">{t("settings")}</Form.Label>
                    </Col>

                    <Col xs="auto">
                      <Form.Check
                        type="checkbox"
                        label={t("iterate")}
                        className="mb-0"
                        checked={autoIterate}
                        onChange={(e) => setAutoIterate(e.target.checked)}
                      />
                    </Col>
                  </Row>

                  <Form.Group as={Row} className="mb-3" controlId="newItemKant">
                    <Form.Label column sm={3}>
                      {t("edgeType")}
                    </Form.Label>
                    <Col sm={3}>
                      <Form.Select
                        value={newItem.kant.slice(1, -1).split(",")[0]}
                        onChange={(e) => {
                          const [, h = "0", s = "0"] =
                            newItem.kant && typeof newItem.kant === "string"
                              ? newItem.kant.slice(1, -1).split(",")
                              : ["-", "0", "0"];
                          setNewItem((prev) => ({
                            ...prev,
                            kant: `[${e.target.value},${h},${s}]`
                          }));
                        }}
                      >
                        {["-", "04", "2", "1", "42"].map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col sm={3}>
                      <Form.Select
                        value={newItem.kant.slice(1, -1).split(",")[1]}
                        onChange={(e) => {
                          const [type = "-", , s = "0"] =
                            newItem.kant && typeof newItem.kant === "string"
                              ? newItem.kant.slice(1, -1).split(",")
                              : ["-", "0", "0"];
                          setNewItem((prev) => ({
                            ...prev,
                            kant: `[${type},${e.target.value},${s}]`
                          }));
                        }}
                      >
                        {[0, 1, 2].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col sm={3}>
                      <Form.Select
                        value={newItem.kant.slice(1, -1).split(",")[2]}
                        onChange={(e) => {
                          const [type = "-", h = "0"] =
                            newItem.kant && typeof newItem.kant === "string"
                              ? newItem.kant.slice(1, -1).split(",")
                              : ["-", "0", "0"];
                          setNewItem((prev) => ({
                            ...prev,
                            kant: `[${type},${h},${e.target.value}]`
                          }));
                        }}
                      >
                        {[0, 1, 2].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </div>
              )}

              <QuantityRuleBuilder
                value={String(newItem.qty ?? "")}
                onChange={(qty) =>
                  setNewItem((previous) => ({ ...previous, qty }))
                }
                settings={expressionSettings}
                t={t}
              />

                </Tab>
                <Tab eventKey="size" title={t("dimensions")}>
                  <PositionRuleBuilder hideTitle kind={t("dimensions")}
                    key={`size-${editingItemId ?? "new"}`}
                    value={newItem.size || "[100,100,100]"}
                    onChange={handleSizeRuleChange} onValidityChange={setIsSizeRuleValid}
                    settings={settingsList} />
                </Tab>
                <Tab eventKey="position" title={t("position")}>
                  <PositionRuleBuilder hideTitle
                    key={`position-${editingItemId ?? "new"}`}
                    settings={settingsList} value={newItem.positionRuleDsl}
                    onChange={handlePositionRuleChange} onValidityChange={handlePositionRuleValidityChange} />
                </Tab>
                <Tab eventKey="rotation" title={t("rotation")}>
                  <p className="text-muted">{t("rotationQuarterTurns")}</p>
                  <PositionRuleBuilder hideTitle kind={t("rotation")}
                    key={`rotation-${editingItemId ?? "new"}`}
                    settings={settingsList} value={newItem.rotationRuleDsl}
                    onChange={handleRotationRuleChange} onValidityChange={setIsRotationRuleValid} />
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeItemEditor}>
                {t("cancel")}
              </Button>
              <Button variant="primary" onClick={addItem}>
                {editingIndex != null ? t("save") : t("add")}
              </Button>
            </Modal.Footer>
          </Modal>

          <section className="mb-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h4 className="mb-0">{t("scriptItems")}</h4>
              <Button variant="primary" onClick={openNewItemEditor}>
                {t("addNewItem")}
              </Button>
            </div>
            <ScriptItemsCards
              hiddenItems={hiddenItems}
              items={rawItems}
              onDelete={handleDeleteItem}
              onEdit={openItemEditor}
              onToggleVisibility={toggleVisibility}
              t={t}
            />
          </section>

          <section className="mb-4">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <h4 className="mb-0">{t("settings")}</h4>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowSettingsList((visible) => !visible)}
                  aria-expanded={showSettingsList}
                >
                  {showSettingsList ? t("hide") : t("show")} {t("settings")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowSettingModal(true)}
                >
                  {t("addSetting")}
                </Button>
              </div>
            </div>
          {showSettingsList && <Table bordered striped responsive className="mb-3">
            <thead>
              <tr>
                <th>{t("id")}</th>
                <th>{t("name")}</th>
                <th>{t("defaultValue")}</th>
                <th>{t("delete")}</th>
              </tr>
            </thead>
            <tbody>
              {parsedSettings.length === 0 ? (
                <tr>
                  <td colSpan="4">{t("noSettings")}</td>
                </tr>
              ) : (
                parsedSettings.map((s) => {
                  const name =
                    settingsList.find(
                      (x) => String(x.settingId) === String(s.settingId)
                    )?.name || "";
                  return (
                    <tr
                      key={s.settingId}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setEditingSettingId(s.settingId);
                        setNewSettingPair({ id: s.settingId, value: s.value });
                        setShowSettingModal(true);
                      }}
                    >
                      <td>{s.settingId}</td>
                      <td>{name}</td>
                      <td>{s.value}</td>
                      <td>
                        <IonIcon
                          icon={trash}
                          style={{ cursor: "pointer", color: "red" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveSetting(s.settingId);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>}
          </section>

          <Modal
            show={showSettingModal}
            onHide={() => {
              setShowSettingModal(false);
              setEditingSettingId(null);
              setNewSettingPair({ id: "", value: "" });
            }}
            className="custom-backdrop"
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {editingSettingId ? t("editSetting") : t("addSetting")}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {/* ID mező szerkesztésnél tiltva */}
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>
                  {t("id")}
                </Form.Label>
                <Col sm={9}>
                  <Form.Select
                    value={newSettingPair.id}
                    disabled={!!editingSettingId}
                    onChange={(e) =>
                      setNewSettingPair((p) => ({ ...p, id: e.target.value }))
                    }
                  >
                    <option value="">{t("chooseId")}</option>
                    {availableSettings.map((s) => (
                      <option key={s.settingId} value={s.settingId}>
                        {s.settingId} – {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>
                  {t("defaultValue")}
                </Form.Label>
                <Col sm={9}>
                  <Form.Control
                    type="text"
                    value={newSettingPair.value}
                    onChange={(e) =>
                      setNewSettingPair((p) => ({
                        ...p,
                        value: e.target.value
                      }))
                    }
                  />
                </Col>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowSettingModal(false);
                  setEditingSettingId(null);
                  setNewSettingPair({ id: "", value: "" });
                }}
              >
                {t("cancel")}
              </Button>
              <Button variant="primary" onClick={saveSettingPair}>
                {editingSettingId ? t("save") : t("add")}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
    </div>
  );
}
